import { Directive, HostListener } from '@angular/core';

/**
 * Ce qui attend la fermeture du message : la ligne réglée, ce qui part alors
 * (`commit`, l'étape d'une plante, le rangement) et ce qui défait l'écriture
 * déjà partie (`revert`, la réouverture d'un geste, une fois l'écriture finie).
 */
export interface PendingUndo {
  key: string;
  /** Clé de traduction du message (`vwPlural` avec `count`). */
  message: string;
  /** Les lignes que l'action emporte quand elle en emporte plusieurs (« Tout ranger ») ; sinon la seule `key`. */
  rows?: string[];
  /** Le nombre de choses réglées, pour un message au pluriel. */
  count?: number;
  commit?: () => Promise<unknown>;
  revert?: () => Promise<unknown>;
}

/**
 * Une liste dont les lignes partent avec « Annuler » (`vw-undo-snackbar`) :
 * « Mes tâches », l'accueil, les cultures finies.
 *
 * Une ligne réglée reste barrée tant que le message est là ; une action
 * différée (`commit`) ne part qu'à la fermeture du message. Un seul message à
 * la fois : régler une autre ligne règle la précédente. Une écriture refusée
 * rétablit la ligne.
 *
 * Une ligne réglée attend la fin du geste en cours pour partir : le clic qu'un
 * téléphone tire d'un toucher arrive au doigt levé, à l'endroit touché ; si la
 * liste avait remonté entre-temps, il toucherait la ligne voisine.
 */
@Directive()
export abstract class UndoableList {
  pendingUndo: PendingUndo | null = null;
  /** Lignes cochées : barrées tant que le message est là. */
  readonly checked = new Set<string>();
  /** Lignes réglées, encore barrées jusqu'à la fin du geste en cours. */
  private readonly leaving = new Set<string>();
  /** Lignes réglées : elles ont quitté la liste. */
  protected readonly gone = new Set<string>();
  /** Un doigt est posé : rien ne quitte la liste. */
  private pointerDown = false;

  /** « Annuler » : les lignes reviennent ; une écriture est défaite, une action différée n'a rien envoyé. */
  onUndo(key: string) {
    const undo = this.pendingUndo;
    if (undo?.key !== key) return;
    this.pendingUndo = null;
    const rows = rowsOf(undo);
    rows.forEach((row) => this.restore(row));
    undo.revert?.().catch(() => rows.forEach((row) => this.leave(row)));
  }

  /** Le message se ferme (délai, toucher ailleurs, écran quitté) : l'action est acquise. */
  onUndoClosed(key: string) {
    if (this.pendingUndo?.key === key) this.settle();
  }

  /** Un doigt se pose : le message en attente peut se fermer, la liste ne bouge pas encore. */
  @HostListener('document:pointerdown')
  onPointerDown() {
    this.pointerDown = true;
  }

  /** Fin du geste : son clic, ou le défilement qui l'annule. Les lignes réglées partent ensuite. */
  @HostListener('document:click')
  @HostListener('document:pointercancel')
  onGestureEnd() {
    this.pointerDown = false;
    if (this.leaving.size) this.leaveSoon();
  }

  /** L'app passe en arrière-plan, où le système peut la fermer : l'action en attente part maintenant. */
  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.settle();
      return;
    }
    this.onVisible();
  }

  @HostListener('window:pagehide')
  onPageHide() {
    this.settle();
  }

  /** L'app revient au premier plan. */
  protected onVisible(): void {}

  /** Le compte de la liste a changé : l'écran qui en tient un le recalcule. */
  protected countChanged(): void {}

  /** Règle le message en cours : ses lignes partent, l'action différée est envoyée. */
  protected settle() {
    const undo = this.pendingUndo;
    if (!undo) return;
    this.pendingUndo = null;
    const rows = rowsOf(undo);
    rows.forEach((row) => this.leave(row));
    undo.commit?.().catch(() => rows.forEach((row) => this.restore(row)));
  }

  protected check(uid: string) {
    this.checked.add(uid);
    this.countChanged();
  }

  /** La ligne revient, décochée, dans la liste et le compte ; son message s'en va. */
  protected restore(uid: string) {
    this.checked.delete(uid);
    this.leaving.delete(uid);
    this.gone.delete(uid);
    if (this.pendingUndo?.key === uid) this.pendingUndo = null;
    this.countChanged();
  }

  /** Une ligne réglée sort du compte tout de suite, de la liste une fois le geste fini. */
  private leave(uid: string) {
    this.checked.add(uid);
    this.leaving.add(uid);
    this.countChanged();
    this.leaveSoon();
  }

  /** Après la tâche en cours, où le clic d'un toucher peut encore venir, et si aucun doigt n'est posé. */
  private leaveSoon() {
    setTimeout(() => {
      if (this.pointerDown) return;
      for (const uid of this.leaving) {
        this.checked.delete(uid);
        this.gone.add(uid);
      }
      this.leaving.clear();
    });
  }
}

function rowsOf(undo: PendingUndo): string[] {
  return undo.rows ?? [undo.key];
}
